import { observer } from 'mobx-react-lite';
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { readMessagingDraft, writeMessagingDraft } from '~services/messagingDraftStorage';
import { useStores } from '~store';

type MessagingDraftContextValue = {
  clearDraft: (draftKey: string) => void;
  draftFor: (draftKey: string) => string;
  hideDraft: (draftKey: string) => void;
  saveDraft: (draftKey: string, content: string) => void;
};

const MessagingDraftContext = createContext<MessagingDraftContextValue | undefined>(
  undefined,
);

/** Keyed by user id in `sessionStorage` so a reload keeps a draft but the next person to sign in never sees it. */
export const MessagingDraftProvider = observer(
  ({ children }: { children: ReactNode }) => {
    const { userStore } = useStores();
    const userId = userStore.user?.id;

    // A ref, not state: the composer reads its draft once on mount, so a keystroke here must not re-render the page.
    const draftsRef = useRef<Record<string, string>>({});

    const memoryKey = useCallback(
      (draftKey: string) => `${userId ?? ''}:${draftKey}`,
      [userId],
    );

    const draftFor = useCallback(
      (draftKey: string) =>
        draftsRef.current[memoryKey(draftKey)] ??
        (userId ? readMessagingDraft(userId, draftKey) : null) ??
        '',
      [memoryKey, userId],
    );

    const saveDraft = useCallback(
      (draftKey: string, content: string) => {
        draftsRef.current[memoryKey(draftKey)] = content;

        if (userId) {
          writeMessagingDraft(userId, draftKey, content);
        }
      },
      [memoryKey, userId],
    );

    const clearDraft = useCallback(
      (draftKey: string) => saveDraft(draftKey, ''),
      [saveDraft],
    );

    // Only the in-memory copy goes, so a reload still finds the stored draft.
    const hideDraft = useCallback(
      (draftKey: string) => {
        draftsRef.current[memoryKey(draftKey)] = '';
      },
      [memoryKey],
    );

    const value = useMemo(
      () => ({ clearDraft, draftFor, hideDraft, saveDraft }),
      [clearDraft, draftFor, hideDraft, saveDraft],
    );

    return (
      <MessagingDraftContext.Provider value={value}>
        {children}
      </MessagingDraftContext.Provider>
    );
  },
);

export const useMessagingDraft = () => {
  const context = useContext(MessagingDraftContext);

  if (!context) {
    throw new Error('useMessagingDraft requires a MessagingDraftProvider');
  }

  return context;
};

export const useMessagingDraftContent = (draftKey: string) => {
  const { clearDraft, draftFor, hideDraft, saveDraft } = useMessagingDraft();
  const [content, setContent] = useState(() => draftFor(draftKey));
  const [seededDraftKey, setSeededDraftKey] = useState(draftKey);
  const hasEditedRef = useRef(false);
  const latestContentRef = useRef(content);
  latestContentRef.current = content;

  if (seededDraftKey !== draftKey) {
    setSeededDraftKey(draftKey);
    setContent(draftFor(draftKey));
  }

  // The user can hydrate after the composer mounts, and only an untouched box may take the stored draft then.
  useEffect(() => {
    if (hasEditedRef.current) return;

    setContent((current) => current || draftFor(draftKey));
  }, [draftFor, draftKey]);

  const changeContent = useCallback(
    (nextContent: string) => {
      hasEditedRef.current = true;
      setContent(nextContent);
      saveDraft(draftKey, nextContent);
    },
    [draftKey, saveDraft],
  );

  const clearContent = useCallback(() => {
    hasEditedRef.current = true;
    setContent('');
    clearDraft(draftKey);
  }, [clearDraft, draftKey]);

  const hideDraftWhileSending = useCallback(
    () => hideDraft(draftKey),
    [draftKey, hideDraft],
  );

  const restoreUnsentContent = useCallback(
    (unsentContent: string) => {
      const restored = latestContentRef.current || unsentContent;

      setContent(restored);
      saveDraft(draftKey, restored);
    },
    [draftKey, saveDraft],
  );

  return {
    changeContent,
    clearContent,
    content,
    hideDraftWhileSending,
    restoreUnsentContent,
  };
};
